import React, { useState } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import inquiryService from '../../services/inquiryService';
import './InquiryModal.scss';

const { TextArea } = Input;
const { Option } = Select;

const InquiryModal = ({
  visible,
  onClose,
  inquiryType = 'GENERAL',
  productId = null,
  productName = '',
  serviceId = null,
  serviceName = '',
  partnerId = null,
  partnerName = '',
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data = {
        ...values,
        inquiry_type: inquiryType,
        product_id: productId,
        product_name: productName,
        service_id: serviceId,
        service_name: serviceName,
        partner_id: partnerId,
        partner_name: partnerName,
      };

      await inquiryService.createInquiry(data);
      message.success('Your inquiry has been submitted. We will respond shortly!');
      form.resetFields();
      onClose();
    } catch (error) {
      if (error.response?.data) {
        const errors = error.response.data;
        const errorMessages = Object.values(errors).flat().join(', ');
        message.error(errorMessages || 'Failed to submit inquiry');
      } else if (error.errorFields) {
        // Validation error
      } else {
        message.error('Failed to submit inquiry. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (inquiryType) {
      case 'PRODUCT':
        return `Inquiry about: ${productName}`;
      case 'SERVICE':
        return `Inquiry about: ${serviceName}`;
      default:
        return 'Contact Animap';
    }
  };

  const getSubjectPlaceholder = () => {
    switch (inquiryType) {
      case 'PRODUCT':
        return 'E.g., Question about product availability, shipping, etc.';
      case 'SERVICE':
        return 'E.g., Question about service details, pricing, availability, etc.';
      default:
        return 'What is your question about?';
    }
  };

  return (
    <Modal
      title={
        <span>
          <QuestionCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {getTitle()}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Submit Inquiry"
      confirmLoading={loading}
      width={600}
      className="inquiry-modal"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          contact_email: '',
          contact_phone: '',
        }}
      >
        <Form.Item
          name="subject"
          label="Subject"
          rules={[{ required: true, message: 'Please enter a subject' }]}
        >
          <Input placeholder={getSubjectPlaceholder()} maxLength={255} />
        </Form.Item>

        <Form.Item
          name="message"
          label="Your Message"
          rules={[{ required: true, message: 'Please enter your message' }]}
        >
          <TextArea
            rows={5}
            placeholder="Describe your question or inquiry in detail..."
            maxLength={2000}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="contact_email"
          label="Alternative Contact Email (optional)"
          rules={[{ type: 'email', message: 'Please enter a valid email' }]}
        >
          <Input placeholder="If you prefer to be contacted at a different email" />
        </Form.Item>

        <Form.Item
          name="contact_phone"
          label="Contact Phone (optional)"
        >
          <Input placeholder="Your phone number for faster response" maxLength={20} />
        </Form.Item>
      </Form>

      {(productName || serviceName) && (
        <div className="inquiry-context">
          <strong>Related to:</strong>
          {productName && <div>Product: {productName}</div>}
          {serviceName && <div>Service: {serviceName}</div>}
          {partnerName && <div>Partner: {partnerName}</div>}
        </div>
      )}
    </Modal>
  );
};

export default InquiryModal;
